import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { ArrowRightIcon, UploadIcon, CameraIcon, CheckCircleIcon, XIcon } from 'lucide-react';
type InbornInputsProps = {
  onComplete: () => void;
};
export const InbornInputs: React.FC<InbornInputsProps> = ({
  onComplete
}) => {
  const [completedMethods, setCompletedMethods] = useState<string[]>([]);
  const [activeMethod, setActiveMethod] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const methods = [{
    id: 'astrology',
    title: 'Astrology',
    description: 'Your birth chart reveals natural talents and tendencies',
    icon: '✨',
    color: 'purple',
    requirements: ['Birth date', 'Birth time', 'Birth location']
  }, {
    id: 'numerology',
    title: 'Numerology',
    description: 'Numbers in your birth date and name influence your path',
    icon: '🔢',
    color: 'blue',
    requirements: ['Full name', 'Birth date']
  }, {
    id: 'human-design',
    title: 'Human Design',
    description: 'Discover your energy type and decision-making strategy',
    icon: '⚡',
    color: 'orange',
    requirements: ['Birth date', 'Birth time', 'Birth location']
  }, {
    id: 'dmit',
    title: 'DMIT',
    description: 'Dermatoglyphics reveals innate talents through fingerprints',
    icon: '👆',
    color: 'green',
    requirements: ['Fingerprint scans']
  }, {
    id: 'iridology',
    title: 'Iridology',
    description: 'Eye patterns reveal physical and psychological traits',
    icon: '👁️',
    color: 'gold',
    requirements: ['Clear eye photos']
  }];
  const completeMethod = (id: string) => {
    if (!completedMethods.includes(id)) {
      setCompletedMethods([...completedMethods, id]);
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
        setActiveMethod(null);
      }, 3000);
    }
  };
  const allCompleted = completedMethods.length === methods.length;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-1120">
      <div className="mb-8" data-id="element-1121">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-1122">
          Inborn Inputs
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-1123">
          Complete these assessments to reveal your natural traits and
          tendencies
        </p>
      </div>
      {showConfetti && <div className="fixed inset-0 pointer-events-none z-50" data-id="element-1124">
          {[...Array(50)].map((_, i) => <motion.div key={i} initial={{
        x: Math.random() * window.innerWidth,
        y: -20,
        opacity: 1
      }} animate={{
        y: window.innerHeight + 20,
        opacity: 0
      }} transition={{
        duration: Math.random() * 2 + 1,
        ease: 'easeOut'
      }} className="absolute w-3 h-3 rounded-full" style={{
        backgroundColor: ['#FF5252', '#FFD700', '#4CAF50', '#2196F3', '#9C27B0'][Math.floor(Math.random() * 5)]
      }} data-id="element-1125" />)}
        </div>}
      {activeMethod ? <MethodDetail method={methods.find(m => m.id === activeMethod)!} onComplete={() => completeMethod(activeMethod)} onBack={() => setActiveMethod(null)} data-id="element-1126" /> : <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1127">
            {methods.map(method => {
          const isCompleted = completedMethods.includes(method.id);
          return <motion.div key={method.id} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => !isCompleted && setActiveMethod(method.id)} data-id="element-1128">
                  <Card className={`cursor-pointer h-full ${isCompleted ? 'border-[#19D3DA]' : ''}`} data-id="element-1129">
                    <div className="flex justify-between items-start mb-4" data-id="element-1130">
                      <div className="text-3xl" data-id="element-1131">{method.icon}</div>
                      {isCompleted ? <Badge color="green" data-id="element-1132">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-1133" />
                          Completed
                        </Badge> : <Badge color={method.color as any} data-id="element-1134">Required</Badge>}
                    </div>
                    <h3 className="text-lg font-semibold mb-1" data-id="element-1135">
                      {method.title}
                    </h3>
                    <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-1136">
                      {method.description}
                    </p>
                    <button className={`flex items-center text-sm font-medium rounded-full py-1 px-3 ${isCompleted ? 'bg-[#19D3DA]/20 text-[#19D3DA]' : 'bg-[#7D56F4] text-white'}`} data-id="element-1137">
                      {isCompleted ? 'Review' : 'Start'}
                      {!isCompleted && <ArrowRightIcon size={14} className="ml-1" data-id="element-1138" />}
                    </button>
                  </Card>
                </motion.div>;
        })}
          </div>
          {allCompleted && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-8 flex justify-center" data-id="element-1139">
              <button onClick={onComplete} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full flex items-center" data-id="element-1140">
                <span data-id="element-1141">Continue to Next Level</span>
                <ArrowRightIcon size={18} className="ml-2" data-id="element-1142" />
              </button>
            </motion.div>}
        </>}
    </motion.div>;
};
type MethodDetailProps = {
  method: {
    id: string;
    title: string;
    description: string;
    icon: string;
    color: string;
    requirements: string[];
  };
  onComplete: () => void;
  onBack: () => void;
};
const MethodDetail: React.FC<MethodDetailProps> = ({
  method,
  onComplete,
  onBack
}) => {
  const [step, setStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const handleUpload = () => {
    setIsUploading(true);
    setTimeout(() => {
      setIsUploading(false);
      setStep(1);
    }, 2000);
  };
  const handleProcess = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      onComplete();
    }, 3000);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} data-id="element-1143">
      <button onClick={onBack} className="mb-4 flex items-center text-[#B8C0E0] hover:text-white" data-id="element-1144">
        <ArrowRightIcon size={16} className="mr-1 transform rotate-180" data-id="element-1145" />
        Back to all methods
      </button>
      <Card data-id="element-1146">
        <div className="flex items-center mb-6" data-id="element-1147">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl mr-4 ${method.color === 'purple' ? 'bg-purple-500/20' : method.color === 'blue' ? 'bg-blue-500/20' : method.color === 'orange' ? 'bg-orange-500/20' : method.color === 'green' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`} data-id="element-1148">
            {method.icon}
          </div>
          <div data-id="element-1149">
            <h2 className="text-xl font-bold" data-id="element-1150">{method.title}</h2>
            <p className="text-[#B8C0E0] text-sm" data-id="element-1151">{method.description}</p>
          </div>
        </div>
        {step === 0 ? <>
            <div className="bg-[#1C2541] rounded-lg p-4 mb-6" data-id="element-1152">
              <h3 className="font-semibold mb-2 flex items-center" data-id="element-1153">
                <span className="mr-2" data-id="element-1154">📋</span>
                What You'll Need
              </h3>
              <ul className="space-y-2" data-id="element-1155">
                {method.requirements.map((req, index) => <li key={index} className="flex items-center text-[#B8C0E0]" data-id="element-1156">
                    <div className="w-1 h-1 bg-[#7D56F4] rounded-full mr-2" data-id="element-1157"></div>
                    {req}
                  </li>)}
              </ul>
            </div>
            <div className="space-y-4" data-id="element-1158">
              {method.id === 'dmit' || method.id === 'iridology' ? <div className="border-2 border-dashed border-[#3A506B] rounded-lg p-8 text-center" data-id="element-1159">
                  <div className="flex justify-center mb-4 space-x-4" data-id="element-1160">
                    <button className="bg-[#1C2541] text-white py-2 px-4 rounded-lg flex items-center" data-id="element-1161">
                      <UploadIcon size={18} className="mr-2" data-id="element-1162" />
                      Upload File
                    </button>
                    <button className="bg-[#1C2541] text-white py-2 px-4 rounded-lg flex items-center" data-id="element-1163">
                      <CameraIcon size={18} className="mr-2" data-id="element-1164" />
                      Take Photo
                    </button>
                  </div>
                  <p className="text-[#B8C0E0] text-sm" data-id="element-1165">
                    Upload a clear image or take a photo
                  </p>
                </div> : <>
                  <div className="space-y-4" data-id="element-1166">
                    <div className="space-y-2" data-id="element-1167">
                      <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-1168">
                        Date of Birth
                      </label>
                      <input type="date" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-1169" />
                    </div>
                    {(method.id === 'astrology' || method.id === 'human-design') && <>
                        <div className="space-y-2" data-id="element-1170">
                          <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-1171">
                            Time of Birth
                          </label>
                          <input type="time" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-1172" />
                        </div>
                        <div className="space-y-2" data-id="element-1173">
                          <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-1174">
                            Place of Birth
                          </label>
                          <input type="text" placeholder="City, Country" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-1175" />
                        </div>
                      </>}
                    {method.id === 'numerology' && <div className="space-y-2" data-id="element-1176">
                        <label className="block text-sm font-medium text-[#B8C0E0]" data-id="element-1177">
                          Full Name (as on birth certificate)
                        </label>
                        <input type="text" className="w-full rounded-lg bg-[#1C2541] border border-[#3A506B] p-3 text-white" data-id="element-1178" />
                      </div>}
                  </div>
                </>}
              <div className="flex justify-end mt-6" data-id="element-1179">
                <button onClick={handleUpload} disabled={isUploading} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-2 px-6 rounded-full flex items-center" data-id="element-1180">
                  {isUploading ? <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-1181"></div>
                      Uploading...
                    </> : <>
                      <span data-id="element-1182">Submit</span>
                      <ArrowRightIcon size={16} className="ml-2" data-id="element-1183" />
                    </>}
                </button>
              </div>
            </div>
          </> : <>
            <div className="text-center py-8" data-id="element-1184">
              {isProcessing ? <div className="space-y-6" data-id="element-1185">
                  <div className="w-16 h-16 border-4 border-[#7D56F4] border-t-transparent rounded-full animate-spin mx-auto" data-id="element-1186"></div>
                  <h3 className="text-xl font-bold" data-id="element-1187">Processing Your Data</h3>
                  <p className="text-[#B8C0E0]" data-id="element-1188">
                    Please wait while we analyze your{' '}
                    {method.title.toLowerCase()} information...
                  </p>
                </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="space-y-6" data-id="element-1189">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto" data-id="element-1190">
                    <CheckCircleIcon size={40} className="text-green-500" data-id="element-1191" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-1192">Data Received!</h3>
                  <p className="text-[#B8C0E0]" data-id="element-1193">
                    Your {method.title.toLowerCase()} information has been
                    successfully uploaded.
                  </p>
                  <button onClick={handleProcess} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-2 px-6 rounded-full inline-flex items-center" data-id="element-1194">
                    <span data-id="element-1195">Process My Data</span>
                    <ArrowRightIcon size={16} className="ml-2" data-id="element-1196" />
                  </button>
                </motion.div>}
            </div>
          </>}
      </Card>
    </motion.div>;
};